// @flow

import * as React from 'react';

import clsx from 'clsx';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import {
  useTranslator,
  withTranslator,
  type TranslatorProps,
} from '@gandi/translate';
import { Base, Hide, User } from '@gandi/ui';

import { withBasename, type WithBasenameProps } from 'src/helpers/withBasename';
import { withRouter, type WithRouterProps } from 'src/helpers/withRouter';

import { selectUsername, selectMobileUserItems } from '../../../modules/app';

import type { FullState } from '../../../types';
import { useNavigationTree } from '../../hooks';
import type {
  NavigationItem as NavigationItemType,
  NavigationItems,
} from '../../types';
import { Controls } from '../Controls';
import { NavigationItem } from '../NavigationItem';

import styles from './Navigation.scss';

type OwnProps = {
  className?: string,
  innerRef?: React.ElementRef<any>,
};

type InjectedProps = {
  /* eslint-disable react/no-unused-prop-types */
  // required in mapstatetoprops
  translator: TranslatorProps,
  /* eslint-enable react/no-unused-prop-types */
  ...WithRouterProps<>,
  ...WithBasenameProps,
};

type StateProps = {
  isAuthenticated: boolean,
  username: string,
  userItems?: NavigationItems,
};

export type Props = {
  ...OwnProps,
  ...InjectedProps,
  ...StateProps,
};

function RenderItem({
  item,
  idx,
  subIdx,
}: {
  item: NavigationItemType,
  idx: number,
  subIdx?: number,
  ...
}) {
  return (
    <Base
      key={item.title}
      id={`navigation-item-${idx}${subIdx || ''}`}
      tagName="li"
      separatorBottom
      className={clsx({ [styles.subMenuItem]: subIdx !== undefined })}
    >
      <NavigationItem item={item} collapsed={false} className={styles.item} />
    </Base>
  );
}

function LinkOrMenu({
  name,
  menu,
  idx,
}: {
  name: string,
  menu: NavigationItemType | NavigationItems,
  idx: number,
  ...
}) {
  return Array.isArray(menu) ? (
    <li key={`navigation-title-${idx}`}>
      <p className={styles.subTitle}>{name}</p>
      <ul className={styles.subMenu}>
        {menu.map((item, subIdx) => (
          <RenderItem item={item} idx={idx} key={item.title} subIdx={subIdx} />
        ))}
      </ul>
    </li>
  ) : (
    <RenderItem item={menu} idx={idx} key={menu.title} />
  );
}

function RenderMainItems() {
  const { __ } = useTranslator();
  const items = useNavigationTree();

  const ariaLabel = __(
    '<for-translators-use>Admin accessibility landmark</for-translators-use>Products',
  );

  return (
    <nav aria-label={ariaLabel} id="layout-secondary-navigation">
      <ul className={styles.mainItems}>
        {Object.entries(items).map(([name, menu], idx) => (
          <LinkOrMenu
            name={name}
            menu={menu}
            idx={idx}
            key={`main-item-${name}`}
          />
        ))}
      </ul>
    </nav>
  );
}

function RenderUserItems({
  isAuthenticated,
  username,
  userItems,
}: {
  isAuthenticated: boolean,
  username: string,
  userItems?: NavigationItems,
}) {
  const { __ } = useTranslator();

  if (!Array.isArray(userItems) || userItems.length < 1) {
    return null;
  }

  const ariaLabel = __(
    '<for-translators-use>Admin accessibility landmark</for-translators-use>User menu',
  );

  return (
    <Hide from="sm">
      <nav className={styles.userMenu} aria-label={ariaLabel}>
        {isAuthenticated && (
          <User theme="dark" color="white" className={styles.username}>
            {username}
          </User>
        )}
        <ul className={styles.userItems}>
          {userItems.map(item => (
            <li key={`item-${item.title}`}>
              <NavigationItem item={item} type="extra" />
            </li>
          ))}
        </ul>
      </nav>
    </Hide>
  );
}

function Navigation({
  username,
  userItems,
  className,
  innerRef,
  isAuthenticated,
}: Props): React.MixedElement {
  const rootClasses = clsx(styles.root, className);

  return (
    <div ref={innerRef} className={rootClasses} aria-hidden>
      <Controls />
      <div className={styles.items}>
        <RenderMainItems />
        <RenderUserItems
          username={username}
          userItems={userItems}
          isAuthenticated={isAuthenticated}
        />
      </div>
    </div>
  );
}

// forced for tests purposes
Navigation.displayName = 'Navigation';

const mapStateToProps = (
  state: FullState,
  props: InjectedProps,
): StateProps => {
  const {
    translator: { __ },
    location: { pathname, search },
    basename,
  } = props;

  return {
    isAuthenticated: selectIsAuthenticatedUser(state),
    username: selectUsername(state),
    userItems: selectMobileUserItems(state, { __, basename, pathname, search }),
  };
};

export { Navigation as Presenter };

export default (compose(
  withTranslator({ propsNamespace: 'translator' }),
  withRouter,
  // required in mapStateToProps
  withBasename,
  connect(mapStateToProps),
)(Navigation): React.ComponentType<OwnProps>);
